import { Head, router } from '@inertiajs/react'
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import React, { useMemo, useState } from 'react'
import Layout from './Layout'
import GuestForm from './Components/GuestForm'
import Button from '../../Components/Button'
import { formatPrice, formatDate } from './utils/formatters'

function StripeCardSection({ stripe }) {
  return (
    <section className="mb-6">
      <label id="card-details-label" className="block text-white text-sm font-medium mb-2">
        Card Details
      </label>
      <div className="bg-white px-4 py-3 rounded-lg min-h-[44px]" aria-labelledby="card-details-label">
        {!stripe ? (
          <div className="text-gray-500 text-sm" role="status">Loading payment form...</div>
        ) : (
          <CardElement
            options={{
              hidePostalCode: true,
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': { color: '#aab7c4' },
                },
                invalid: { color: '#9e2146' },
              },
            }}
          />
        )}
      </div>
    </section>
  )
}

function StripeDetailsForm(props) {
  const stripe = useStripe()
  const elements = useElements()
  return <DetailsFormInner {...props} stripe={stripe} elements={elements} />
}

function DetailsForm(props) {
  return <DetailsFormInner {...props} stripe={null} elements={null} />
}

function DetailsFormInner({ account, summary, skipStripe, stripe, elements, bookingDepositsEnabled, eventTypeId, packageId, venueId }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  })
  const [enquirySubmitting, setEnquirySubmitting] = useState(false)
  const [bookingSubmitting, setBookingSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const depositAmount = summary.deposit_amount_pence
  const hasDeposit = !!(depositAmount && depositAmount > 0)

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const isSubmitting = enquirySubmitting || bookingSubmitting
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)
  const canSubmit = form.name && form.email && emailValid && !isSubmitting

  const handleEnquiry = () => {
    if (!canSubmit) return
    setEnquirySubmitting(true)
    setError(null)
    router.post(`/${account.slug}/booking/enquiry`, {
      booking: {
        guest_name: form.name,
        guest_email: form.email,
        guest_phone: form.phone,
        message: form.message,
        event_type_id: eventTypeId
      }
    }, {
      onError: (errors) => {
        setError(errors.message || errors.alert || 'Enquiry failed. Please try again.')
        setEnquirySubmitting(false)
      }
    })
  }

  const handleBooking = async () => {
    if (!canSubmit) return
    setBookingSubmitting(true)
    setError(null)

    try {
      let paymentMethodId = null

      if (hasDeposit && !skipStripe) {
        if (!stripe || !elements) return

        const cardElement = elements.getElement(CardElement)
        const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
            name: form.name,
            email: form.email,
            address: { country: 'GB' }
          }
        })

        if (pmError) {
          throw new Error(pmError.message)
        }

        paymentMethodId = paymentMethod.id
      } else if (hasDeposit && skipStripe) {
        paymentMethodId = 'pm_e2e_test_bypass'
      }

      router.post(`/${account.slug}/booking/book`, {
        booking: {
          guest_name: form.name,
          guest_email: form.email,
          guest_phone: form.phone,
          message: form.message,
          stripe_payment_id: paymentMethodId,
          event_type_id: eventTypeId
        }
      }, {
        onError: (errors) => {
          setError(errors.message || errors.alert || 'Booking failed. Please try again.')
          setBookingSubmitting(false)
        }
      })
    } catch (err) {
      setError(err.message)
      setBookingSubmitting(false)
    }
  }

  return (
    <div className="px-4 pt-24 pb-8">
      <button
        onClick={() => router.visit(`/${account.slug}/booking/configure?package_id=${packageId}${venueId ? `&venue_id=${venueId}` : ''}`)}
        className="text-white/70 text-sm mb-4 inline-flex items-center hover:text-white transition"
      >
        <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      {/* Summary Card */}
      <section className="bg-white rounded-xl p-6 mb-6" aria-label="Booking summary">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Your Booking</h2>
        <dl className="space-y-3 text-sm">
          {summary.event_type_name && (
            <div className="flex justify-between">
              <dt className="text-gray-600">Occasion</dt>
              <dd className="font-medium text-gray-900">{summary.event_type_name}</dd>
            </div>
          )}
          {summary.venue_name && (
            <div className="flex justify-between">
              <dt className="text-gray-600">Venue</dt>
              <dd className="font-medium text-gray-900">{summary.venue_name}</dd>
            </div>
          )}
          <div className="flex justify-between">
            <dt className="text-gray-600">Package</dt>
            <dd className="font-medium text-gray-900">{summary.package_name}</dd>
          </div>
          {summary.add_ons?.length > 0 && (
            <div className="flex justify-between">
              <dt className="text-gray-600">Add-ons</dt>
              <dd className="font-medium text-gray-900 text-right">
                {summary.add_ons.map(a => a.name).join(', ')}
              </dd>
            </div>
          )}
          <div className="flex justify-between">
            <dt className="text-gray-600">Date</dt>
            <dd className="font-medium text-gray-900">{formatDate(summary.date)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-600">Time</dt>
            <dd className="font-medium text-gray-900">{summary.time}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-600">Guests</dt>
            <dd className="font-medium text-gray-900">{summary.party_size}</dd>
          </div>
          <div className="flex justify-between pt-2 border-t font-bold text-base">
            <dt className="text-gray-900">Total</dt>
            <dd className="text-sky-900">{formatPrice(summary.total_pence)}</dd>
          </div>
          {hasDeposit && (
            <div className="flex justify-between text-sm">
              <dt className="text-gray-600">Deposit required</dt>
              <dd className="font-semibold text-sky-700">{formatPrice(depositAmount)}</dd>
            </div>
          )}
        </dl>
      </section>

      {/* Guest Form */}
      <section className="mb-6">
        <h2 className="text-lg font-semibold text-white mb-3">Your Details</h2>
        <GuestForm
          name={form.name}
          email={form.email}
          phone={form.phone}
          message={form.message}
          onChange={handleChange}
        />
      </section>

      {/* Error */}
      {error && (
        <div className="bg-red-500 text-white px-4 py-3 rounded-lg mb-4" role="alert">
          {error}
        </div>
      )}

      {/* Enquiry Option — the mainstream path for weddings and funerals */}
      <div className="pb-4">
        <Button
          onClick={handleEnquiry}
          disabled={!canSubmit}
          variant="primary"
          theme="dark"
          size="lg"
          fullWidth
        >
          {enquirySubmitting ? 'Submitting...' : 'Enquire Now'}
        </Button>
        {!canSubmit && !isSubmitting && (
          <p className="text-sky-200 text-sm text-center mt-2">
            {!form.name ? 'Add your name to continue' : !emailValid ? 'Add a valid email to continue' : ''}
          </p>
        )}
        <p className="text-sky-200 text-xs text-center mt-2">
          Non-binding. Our team will get in touch to confirm availability.
        </p>
      </div>

      {/* OR Divider + Reserve Option - only when deposits are enabled */}
      {bookingDepositsEnabled && (
        <>
          <div className="flex items-center gap-3 my-2">
            <div className="flex-1 border-t border-white/20" />
            <span className="text-sm text-sky-200 font-medium">OR</span>
            <div className="flex-1 border-t border-white/20" />
          </div>

          <div className="pt-4 pb-4">
            <p className="text-sm text-sky-200 mb-3 text-center">Reserve instantly{hasDeposit ? ` with a ${formatPrice(depositAmount)} deposit` : ''}</p>

            {hasDeposit && !skipStripe && (
              <StripeCardSection stripe={stripe} />
            )}

            <Button
              onClick={handleBooking}
              disabled={!canSubmit || (hasDeposit && !skipStripe && !stripe)}
              variant="secondary"
              theme="dark"
              size="lg"
              fullWidth
            >
              {bookingSubmitting ? 'Processing...' : hasDeposit ? `Pay ${formatPrice(depositAmount)} & Reserve` : 'Reserve Now'}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

export default function Details({ account, summary, stripe_key, skip_stripe, booking_deposits_enabled, event_type_id, package_id, venue_id, meta }) {
  const hasDeposit = booking_deposits_enabled && summary.deposit_amount_pence && summary.deposit_amount_pence > 0

  const stripePromise = useMemo(() => {
    if (skip_stripe || !stripe_key || !hasDeposit) return null
    return loadStripe(stripe_key)
  }, [stripe_key, skip_stripe, hasDeposit])

  const formProps = {
    account,
    summary,
    bookingDepositsEnabled: booking_deposits_enabled,
    eventTypeId: event_type_id,
    packageId: package_id,
    venueId: venue_id
  }

  return (
    <>
      <Head title={meta.title} />
      <Layout account={account}>
        {hasDeposit && !skip_stripe ? (
          <Elements stripe={stripePromise}>
            <StripeDetailsForm {...formProps} skipStripe={false} />
          </Elements>
        ) : (
          <DetailsForm {...formProps} skipStripe={skip_stripe || !hasDeposit} />
        )}
      </Layout>
    </>
  )
}
